/** UI strings for the F08 models (bound states in wells, barrier tunnelling); merged into the photons dictionary. */
export const sv = {
  wsTitle: "Bundna tillstånd: låda, ändlig potentialbrunn och oscillator",
  wsNote:
    "Samma bredd L och massa i alla tre vyerna. Energier i eV med CODATA-konstanter; ψ och |ψ|² ritas i enheter av 1/√L och 1/L. Oscillatorn är vald så att dess grundenergi är lådans E₁ (ħω = 2E₁).",
  wsViewBox: "Oändlig potentialbrunn (låda)",
  wsViewFinite: "Ändlig potentialbrunn",
  wsViewOscillator: "Harmonisk oscillator",
  wsParticle: "Partikel",
  wsElectron: "Elektron",
  wsProton: "Proton",
  wsWidth: "Bredd L (nm)",
  wsLevel: "Kvanttal n",
  wsOscLevel: "Oscillatorns kvanttal n",
  wsDepth: "Djup U₀/E₁,∞",
  wsFrom: "Intervallets början x/L",
  wsTo: "Intervallets slut x/L",
  wsBoxSvg: "Lådans nivåer och vågfunktion",
  wsBoxDesc:
    "Till vänster nivåerna n = 1 till 8 i skala med n², den valda markerad. Till höger vågfunktionen ψₙ (streckad) och sannolikhetstätheten |ψₙ|² (heldragen) mellan de oändliga väggarna, med det valda intervallet skuggat.",
  wsFiniteSvg: "Ändlig potentialbrunn: nivåer och vågfunktion",
  wsFiniteDesc:
    "Brunnen med djupet U₀ och alla bundna nivåer som heldragna linjer. Korta streckade linjer till höger visar lådans nivåer med samma bredd. Det valda tillståndets vågfunktion ritas kring sin nivå, med exponentiella svansar utanför väggarna.",
  wsOscSvg: "Oscillator och låda med samma grundenergi",
  wsOscDesc:
    "Parabeln U = k′x²/2 med oscillatorns nivåer (heldragna) och lådans nivåer (streckade) med väggar vid ±L/2, energi i enheter av ħω. Det valda oscillatortillståndets vågfunktion ritas kring sin nivå; korta lodräta streck markerar de klassiska vändpunkterna.",
  wsAxisEnergy: "E",
  wsSymbolU0: "U₀",
  wsAxisOscEnergy: "E/(ħω)",
  tbSymbolE: "E",
  tbSymbolL: "L",
  wsAxisX: "x/L",
  wsAxisXi: "x/b",
  wsWall: "U = ∞",
  wsEnergy: "Eₙ = n²h²/(8mL²) (eV)",
  wsEnergyRatio: "Eₙ/E₁",
  wsAmplitude: "Normeringskonstant √(2/L) (nm^(−1/2))",
  wsDeBroglie: "de Broglie-våglängd 2L/n (nm)",
  wsAbsorption: "De fem längsta absorptionsvåglängderna från n (nm)",
  wsNodes: "Noder innanför väggarna",
  wsProbability: "Sannolikhet i intervallet",
  wsBound: "Antal bundna tillstånd",
  wsZ0: "z₀ = (L/2)√(2mU₀)/ħ",
  wsDepthEv: "U₀ (eV)",
  wsShown: "Visat tillstånd",
  wsShownValue: "n = {n}, {parity}",
  wsEven: "jämnt",
  wsOdd: "udda",
  wsMissing: "n = {n} saknas; visar n = {shown}",
  wsFiniteRatio: "E/E₁,∞",
  wsFiniteEnergy: "E (eV)",
  wsBoxSame: "Lådans Eₙ med samma n (eV)",
  wsPenetration: "Inträngningsdjup 1/κ (nm)",
  wsOutside: "Sannolikhet utanför brunnen",
  wsAllRatios: "Alla nivåer E/E₁,∞",
  wsHbarOmega: "ħω = 2E₁ (eV)",
  wsOmega: "ω (s⁻¹)",
  wsSpring: "Fjäderkonstant k′ = mω² (N/m)",
  wsB: "b = √(ħ/(mω)) = A₀ (nm)",
  wsOscEnergy: "Eₙ = (n + ½)ħω (eV)",
  wsTurning: "Vändpunkt Aₙ = b√(2n + 1) (nm)",
  wsOscOutside: "Sannolikhet utanför vändpunkterna",
  wsBoxCompare: "Lådans nivå nummer n + 1 (eV)",
  wsBoxFormula: String.raw`$E_n=\dfrac{n^2h^2}{8mL^2}$ (40.31), $\psi_n=\sqrt{2/L}\,\sin(n\pi x/L)$ (40.35). Sannolikheten mellan $aL$ och $bL$ beror bara på $a$, $b$ och $n$.`,
  wsFiniteFormula: String.raw`$z=kL/2$, $z_0=\tfrac{L}{2}\sqrt{2mU_0}/\hbar=\tfrac{\pi}{2}\sqrt{U_0/E_{1,\infty}}$. Jämna: $z\tan z=\sqrt{z_0^2-z^2}$; udda: $-z\cot z=\sqrt{z_0^2-z^2}$; $E/E_{1,\infty}=4z^2/\pi^2$.`,
  wsOscFormula: String.raw`$E_n=(n+\tfrac12)\hbar\omega$ (40.46), $\omega=\sqrt{k'/m}$. Med $\hbar\omega=2E_1$ blir $b=L/\pi$ för varje massa. Svansen för $n=0$: $P(|x|>A_0)=\operatorname{erfc}1$.`,
  tbTitle: "Barriär och tunnling",
  tbNote:
    "Stationär spridning på en rektangulär barriär: inkommande våg med amplituden 1 från vänster. T beräknas exakt genom att ψ och ψ′ matchas vid båda kanterna; G e^(−2κL) är bokens närmevärde (40.42). CODATA-konstanter.",
  tbViewBarrier: "Barriär",
  tbViewStm: "STM-gap",
  tbEnergy: "Totalenergi E (eV)",
  tbHeight: "Barriärhöjd U₀ (eV)",
  tbWidth: "Barriärbredd L (nm)",
  tbWorkFunction: "Effektiv barriärhöjd φ (eV)",
  tbGapChange: "Ökning av gapet Δs (nm)",
  tbProfileSvg: "Sannolikhetstäthet kring barriären",
  tbProfileDesc:
    "Barriären som en skuggad rektangel mellan x = 0 och x = L och energin E som streckad linje (energiskala till höger). Den heldragna kurvan är |ψ|² för en inkommande våg med amplituden 1: interferens med den reflekterade vågen till vänster, avtagande eller oscillerande inne i barriären och konstant |ψ|² = T till höger.",
  tbLogSvg: "Transmissionskoefficient mot barriärbredd",
  tbLogDesc:
    "log₁₀ T mot barriärbredden L från 0 till 2 nm vid fasta E och U₀: exakt T heldraget, närmevärdet G e^(−2κL) streckat (bara för E < U₀). Punkten markerar den valda bredden.",
  tbStmSvg: "Tunnelström mot gapets ökning",
  tbStmDesc:
    "ln(I/I₀) mot gapets ökning Δs för de effektiva barriärhöjderna 0,6, 0,7 och 3,2 eV från Binnig m.fl. (1982), streckade, och den valda höjden heldragen. Lutningen är −2κ. Den lodräta linjen markerar det valda Δs. Sammanfaller den valda höjden med en publicerad ligger den heldragna linjen ovanpå den streckade.",
  tbAxisX: "x (nm)",
  tbAxisDensity: "|ψ|²",
  tbAxisL: "L (nm)",
  tbAxisLogT: "log₁₀ T",
  tbAxisGap: "Δs (nm)",
  tbAxisLnI: "ln(I/I₀)",
  tbRegime: "Fall",
  tbRegimeTunnel: "E < U₀: tunnling",
  tbRegimeEdge: "E = U₀",
  tbRegimeOver: "E > U₀: över barriären",
  tbK: "k = √(2mE)/ħ (nm⁻¹)",
  tbKappa: "κ = √(2m(U₀ − E))/ħ (nm⁻¹)",
  tbQ: "Vågtal i barriären √(2m(E − U₀))/ħ (nm⁻¹)",
  tbKappaL: "κL",
  tbG: "G = 16(E/U₀)(1 − E/U₀)",
  tbT: "T exakt",
  tbThick: "G e^(−2κL)",
  tbThickRatio: "G e^(−2κL) / T exakt",
  tbR: "R",
  tbSum: "R + T",
  tbKinetic: "Kinetisk energi efter barriären (eV)",
  tbHundred: "Breddökning som sänker T hundra gånger, ln 100/(2κ) (nm)",
  tbNone: "—",
  tbSlope: "Lutning d ln I/ds = −2κ (nm⁻¹)",
  tbRatio: "I(s + Δs)/I(s) = e^(−2κΔs)",
  tbDecade: "Gapökning för en tiopotens, ln 10/(2κ) (nm)",
  tbBinnig: "Binnig m.fl., φ = {phi} eV: I(s + Δs)/I(s)",
  tbBarrierFormula: String.raw`$E<U_0$: $T=\left[1+\dfrac{U_0^2\sinh^2\kappa L}{4E(U_0-E)}\right]^{-1}$; för $\kappa L\gg1$ blir $T\approx Ge^{-2\kappa L}$ (40.42). $E>U_0$: $\sinh\to\sin$ med vågtalet $\sqrt{2m(E-U_0)}/\hbar$ och $U_0-E\to E-U_0$.`,
  tbStmFormula: String.raw`Tunnelströmmen är proportionell mot $T\propto e^{-2\kappa s}$, så $\dfrac{d\ln I}{ds}=-2\kappa$ med $\kappa=\sqrt{2m\varphi}/\hbar$ och den effektiva barriärhöjden $\varphi$.`,
};

export const en: Record<keyof typeof sv, string> = {
  wsTitle: "Bound states: box, finite potential well and oscillator",
  wsNote:
    "Same width L and mass in all three views. Energies in eV with CODATA constants; ψ and |ψ|² are drawn in units of 1/√L and 1/L. The oscillator is chosen so that its ground energy equals the box E₁ (ħω = 2E₁).",
  wsViewBox: "Infinite potential well (box)",
  wsViewFinite: "Finite potential well",
  wsViewOscillator: "Harmonic oscillator",
  wsParticle: "Particle",
  wsElectron: "Electron",
  wsProton: "Proton",
  wsWidth: "Width L (nm)",
  wsLevel: "Quantum number n",
  wsOscLevel: "Oscillator quantum number n",
  wsDepth: "Depth U₀/E₁,∞",
  wsFrom: "Interval start x/L",
  wsTo: "Interval end x/L",
  wsBoxSvg: "Box levels and wave function",
  wsBoxDesc:
    "Left: levels n = 1 to 8 scaled with n², the chosen one marked. Right: the wave function ψₙ (dashed) and the probability density |ψₙ|² (solid) between the infinite walls, with the chosen interval shaded.",
  wsFiniteSvg: "Finite potential well: levels and wave function",
  wsFiniteDesc:
    "The well of depth U₀ with every bound level as a solid line. Short dashed lines on the right show the box levels with the same width. The chosen state's wave function is drawn around its level, with exponential tails outside the walls.",
  wsOscSvg: "Oscillator and box with the same ground energy",
  wsOscDesc:
    "The parabola U = k′x²/2 with the oscillator levels (solid) and the box levels (dashed) with walls at ±L/2, energy in units of ħω. The chosen oscillator state's wave function is drawn around its level; short vertical marks show the classical turning points.",
  wsAxisEnergy: "E",
  wsSymbolU0: "U₀",
  wsAxisOscEnergy: "E/(ħω)",
  tbSymbolE: "E",
  tbSymbolL: "L",
  wsAxisX: "x/L",
  wsAxisXi: "x/b",
  wsWall: "U = ∞",
  wsEnergy: "Eₙ = n²h²/(8mL²) (eV)",
  wsEnergyRatio: "Eₙ/E₁",
  wsAmplitude: "Normalization constant √(2/L) (nm^(−1/2))",
  wsDeBroglie: "de Broglie wavelength 2L/n (nm)",
  wsAbsorption: "The five longest absorption wavelengths from n (nm)",
  wsNodes: "Nodes between the walls",
  wsProbability: "Probability in the interval",
  wsBound: "Number of bound states",
  wsZ0: "z₀ = (L/2)√(2mU₀)/ħ",
  wsDepthEv: "U₀ (eV)",
  wsShown: "State shown",
  wsShownValue: "n = {n}, {parity}",
  wsEven: "even",
  wsOdd: "odd",
  wsMissing: "n = {n} does not exist; showing n = {shown}",
  wsFiniteRatio: "E/E₁,∞",
  wsFiniteEnergy: "E (eV)",
  wsBoxSame: "Box Eₙ with the same n (eV)",
  wsPenetration: "Penetration depth 1/κ (nm)",
  wsOutside: "Probability outside the well",
  wsAllRatios: "All levels E/E₁,∞",
  wsHbarOmega: "ħω = 2E₁ (eV)",
  wsOmega: "ω (s⁻¹)",
  wsSpring: "Force constant k′ = mω² (N/m)",
  wsB: "b = √(ħ/(mω)) = A₀ (nm)",
  wsOscEnergy: "Eₙ = (n + ½)ħω (eV)",
  wsTurning: "Turning point Aₙ = b√(2n + 1) (nm)",
  wsOscOutside: "Probability outside the turning points",
  wsBoxCompare: "Box level number n + 1 (eV)",
  wsBoxFormula: String.raw`$E_n=\dfrac{n^2h^2}{8mL^2}$ (40.31), $\psi_n=\sqrt{2/L}\,\sin(n\pi x/L)$ (40.35). The probability between $aL$ and $bL$ depends only on $a$, $b$ and $n$.`,
  wsFiniteFormula: String.raw`$z=kL/2$, $z_0=\tfrac{L}{2}\sqrt{2mU_0}/\hbar=\tfrac{\pi}{2}\sqrt{U_0/E_{1,\infty}}$. Even: $z\tan z=\sqrt{z_0^2-z^2}$; odd: $-z\cot z=\sqrt{z_0^2-z^2}$; $E/E_{1,\infty}=4z^2/\pi^2$.`,
  wsOscFormula: String.raw`$E_n=(n+\tfrac12)\hbar\omega$ (40.46), $\omega=\sqrt{k'/m}$. With $\hbar\omega=2E_1$, $b=L/\pi$ for any mass. The tail for $n=0$: $P(|x|>A_0)=\operatorname{erfc}1$.`,
  tbTitle: "Barrier and tunnelling",
  tbNote:
    "Stationary scattering on a rectangular barrier: incoming wave of amplitude 1 from the left. T is computed exactly by matching ψ and ψ′ at both edges; G e^(−2κL) is the book's approximation (40.42). CODATA constants.",
  tbViewBarrier: "Barrier",
  tbViewStm: "STM gap",
  tbEnergy: "Total energy E (eV)",
  tbHeight: "Barrier height U₀ (eV)",
  tbWidth: "Barrier width L (nm)",
  tbWorkFunction: "Effective barrier height φ (eV)",
  tbGapChange: "Gap increase Δs (nm)",
  tbProfileSvg: "Probability density around the barrier",
  tbProfileDesc:
    "The barrier as a shaded rectangle between x = 0 and x = L and the energy E as a dashed line (energy scale on the right). The solid curve is |ψ|² for an incoming wave of amplitude 1: interference with the reflected wave on the left, decaying or oscillating inside the barrier and constant |ψ|² = T on the right.",
  tbLogSvg: "Transmission coefficient against barrier width",
  tbLogDesc:
    "log₁₀ T against the barrier width L from 0 to 2 nm at fixed E and U₀: exact T solid, the approximation G e^(−2κL) dashed (only for E < U₀). The dot marks the chosen width.",
  tbStmSvg: "Tunnelling current against gap increase",
  tbStmDesc:
    "ln(I/I₀) against the gap increase Δs for the effective barrier heights 0.6, 0.7 and 3.2 eV from Binnig et al. (1982), dashed, and the chosen height solid. The slope is −2κ. The vertical line marks the chosen Δs. When the chosen height equals a published one, the solid line lies on top of the dashed one.",
  tbAxisX: "x (nm)",
  tbAxisDensity: "|ψ|²",
  tbAxisL: "L (nm)",
  tbAxisLogT: "log₁₀ T",
  tbAxisGap: "Δs (nm)",
  tbAxisLnI: "ln(I/I₀)",
  tbRegime: "Case",
  tbRegimeTunnel: "E < U₀: tunnelling",
  tbRegimeEdge: "E = U₀",
  tbRegimeOver: "E > U₀: above the barrier",
  tbK: "k = √(2mE)/ħ (nm⁻¹)",
  tbKappa: "κ = √(2m(U₀ − E))/ħ (nm⁻¹)",
  tbQ: "Wave number in the barrier √(2m(E − U₀))/ħ (nm⁻¹)",
  tbKappaL: "κL",
  tbG: "G = 16(E/U₀)(1 − E/U₀)",
  tbT: "T exact",
  tbThick: "G e^(−2κL)",
  tbThickRatio: "G e^(−2κL) / T exact",
  tbR: "R",
  tbSum: "R + T",
  tbKinetic: "Kinetic energy after the barrier (eV)",
  tbHundred: "Width increase that lowers T a hundredfold, ln 100/(2κ) (nm)",
  tbNone: "—",
  tbSlope: "Slope d ln I/ds = −2κ (nm⁻¹)",
  tbRatio: "I(s + Δs)/I(s) = e^(−2κΔs)",
  tbDecade: "Gap increase per factor of ten, ln 10/(2κ) (nm)",
  tbBinnig: "Binnig et al., φ = {phi} eV: I(s + Δs)/I(s)",
  tbBarrierFormula: String.raw`$E<U_0$: $T=\left[1+\dfrac{U_0^2\sinh^2\kappa L}{4E(U_0-E)}\right]^{-1}$; for $\kappa L\gg1$, $T\approx Ge^{-2\kappa L}$ (40.42). $E>U_0$: $\sinh\to\sin$ with the wave number $\sqrt{2m(E-U_0)}/\hbar$ and $U_0-E\to E-U_0$.`,
  tbStmFormula: String.raw`The tunnelling current is proportional to $T\propto e^{-2\kappa s}$, so $\dfrac{d\ln I}{ds}=-2\kappa$ with $\kappa=\sqrt{2m\varphi}/\hbar$ and the effective barrier height $\varphi$.`,
};
